import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/useAuth';
import { ROUTES } from './routes';

export type RequirePasswordCurrentProps = {
  children: ReactNode;
};

/**
 * Blocks the whole application shell while `mustChangePassword` is set
 * (Story I): an install-time or admin-set credential must be replaced before
 * the session can do anything else.
 *
 * **This is the UX, not the enforcement.** The backend's
 * `requirePasswordChanged` middleware refuses every protected route with
 * `403 PASSWORD_CHANGE_REQUIRED` regardless of what the client does, and
 * `mustChangePassword` here comes from `GET /auth/me` — the live database
 * row — not from the token. Deleting this component would change nothing
 * about what the session can actually reach.
 *
 * Placed above `AppShell` so no nav, no module page, and no list request
 * renders behind the redirect.
 */
export function RequirePasswordCurrent({ children }: RequirePasswordCurrentProps) {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.mustChangePassword) {
    return (
      <Navigate
        to={ROUTES.changePassword}
        replace
        // Where to land once the password is set, if it was a deep link.
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <>{children}</>;
}
